import {
  Badge,
  Box,
  Collapse,
  Group,
  Image,
  Paper,
  Spoiler,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { formatPrice } from '@/helpers';
import { resolveMenuItemImage } from '../helpers';
import {
  DESCRIPTION_COLLAPSED_HEIGHT,
  OPTIONS_LABEL,
  SHOW_LESS_LABEL,
  SHOW_MORE_LABEL,
} from '../constants';
import type { MenuItem } from '../types';
import { MenuItemOptionGroups } from './MenuItemOptionGroups';
import { MenuItemImageModal } from './MenuItemImageModal';

type MenuItemRowProps = {
  item: MenuItem;
};

export const MenuItemRow: React.FC<MenuItemRowProps> = ({ item }) => {
  const [optionsOpened, { toggle: toggleOptions }] = useDisclosure(false);
  const [imageOpened, { open: openImage, close: closeImage }] = useDisclosure(false);

  const price = formatPrice(item.preTaxPriceCents);
  const hasOptions = item.optionGroups.length > 0;

  return (
    <Paper withBorder p="md" radius="md">
      <Group gap="md" align="flex-start" wrap="nowrap">
        <UnstyledButton onClick={openImage} aria-label={`View image of ${item.nameForVendor}`}>
          <Image
            src={resolveMenuItemImage(item.primaryImageUrl)}
            alt={item.nameForVendor}
            w={72}
            h={72}
            radius="sm"
            fit="cover"
          />
        </UnstyledButton>

        <Stack gap={2} flex={1} miw={0}>
          <Group gap="xs" align="center" wrap="wrap">
            <Text fw={600}>{item.nameForVendor}</Text>
            {item.dietaryRestrictions.map((restriction) => (
              <Badge key={restriction} variant="light" radius="xl" size="sm" color="gray">
                {restriction}
              </Badge>
            ))}
          </Group>
          {item.description && (
            <Spoiler
              maxHeight={DESCRIPTION_COLLAPSED_HEIGHT}
              showLabel={SHOW_MORE_LABEL}
              hideLabel={SHOW_LESS_LABEL}
              styles={{ control: { fontSize: 'var(--mantine-font-size-sm)' } }}
            >
              <Text>{item.description}</Text>
            </Spoiler>
          )}
        </Stack>

        <Stack gap={8} align="flex-end" miw={0}>
          {price && (
            <Text fw={600} style={{ whiteSpace: 'nowrap' }}>
              {price}
            </Text>
          )}
          {hasOptions && (
            <UnstyledButton onClick={toggleOptions}>
              <Group gap={4} wrap="nowrap">
                <Text size="xs" fw={600} c="blue">
                  {OPTIONS_LABEL}
                </Text>
                {optionsOpened ? (
                  <IconChevronUp size={14} color="var(--mantine-color-blue-6)" />
                ) : (
                  <IconChevronDown size={14} color="var(--mantine-color-blue-6)" />
                )}
              </Group>
            </UnstyledButton>
          )}
        </Stack>
      </Group>

      {hasOptions && (
        <Collapse in={optionsOpened}>
          <Box pt="xs" pl={88}>
            <MenuItemOptionGroups optionGroups={item.optionGroups} />
          </Box>
        </Collapse>
      )}

      <MenuItemImageModal
        opened={imageOpened}
        onClose={closeImage}
        title={item.nameForVendor}
        images={(item.images.length > 0 ? item.images : [item.primaryImageUrl]).map(
          resolveMenuItemImage
        )}
      />
    </Paper>
  );
};
